import React, { useEffect, useLayoutEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import {
  Input, CustomSelect, ConfirmModal, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError, Amount,
} from '@redpanda/ui';
import { useUnsavedChangesWarning } from '@redpanda/utils';
import { createJournalEntry, updateJournalEntry } from '../services/api';
import { JournalEntry, useJournalStoreBase } from '../stores/journalStore';
import { useAccountOptionsStore } from '../stores/accountStore';
import { removeListItem } from '../utils/pendingItem';
import { notifyDataChanged } from '../utils/dataEvents';
import { apiErrorMessage } from '../utils/apiError';
import { dateInputValue, todayInput, toUtcMidnight } from '../utils/dates';

interface JournalDialogProps {
  /** 有值＝編輯（已過帳的只能查看）；沒有＝新增 */
  journal?: JournalEntry;
  /** 只能查看（別人建立的分錄，一般使用者不能改） */
  readOnly?: boolean;
  onClose: () => void;
}

interface DetailForm {
  accountId: string;
  debitAmount: string;
  creditAmount: string;
  description: string;
}

interface FormData {
  date: string;
  description: string;
  reference: string;
  details: DetailForm[];
}

const emptyLine = (): DetailForm => ({ accountId: '', debitAmount: '', creditAmount: '', description: '' });
const amountOf = (value: string) => Number(value) || 0;
/** 金額欄只留數字和第一個小數點（同 @redpanda/utils useNumberInput 的小數規則） */
const sanitizeAmount = (value: string) => value.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1');

/**
 * 新增／編輯分錄：樂觀更新＋立即關閉（§3-17），失敗回滾
 * 借貸平衡、每列只填一邊在前端先擋，後端同規則再檢查一次
 */
const JournalDialog: React.FC<JournalDialogProps> = ({ journal, readOnly: viewOnly = false, onClose }) => {
  const { t } = useTranslation();
  const isEditMode = !!journal;
  const readOnly = !!journal?.isPosted || viewOnly;
  const accounts = useAccountOptionsStore(state => state.accounts);
  const fetchAccounts = useAccountOptionsStore(state => state.fetchAll);
  const { register, handleSubmit, formState: { errors, isDirty }, reset, control, watch, setValue, setError, clearErrors } = useForm<FormData>({
    defaultValues: { date: todayInput(), description: '', reference: '', details: [emptyLine(), emptyLine()] },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'details' });

  // 科目清單：有快取先顯示，每次開啟背景重抓（別人新增的科目也看得到）
  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // 初始值在繪製前帶入（R31）
  useLayoutEffect(() => {
    reset(journal ? {
      date: dateInputValue(journal.date),
      description: journal.description,
      reference: journal.reference || '',
      details: journal.details.map(detail => ({
        accountId: detail.accountId,
        debitAmount: Number(detail.debitAmount) ? String(Number(detail.debitAmount)) : '',
        creditAmount: Number(detail.creditAmount) ? String(Number(detail.creditAmount)) : '',
        description: detail.description || '',
      })),
    } : { date: todayInput(), description: '', reference: '', details: [emptyLine(), emptyLine()] });
  }, [journal, reset]);

  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty && !readOnly, onClose);

  const details = watch('details');
  const totalDebit = details.reduce((sum, d) => sum + amountOf(d.debitAmount), 0);
  const totalCredit = details.reduce((sum, d) => sum + amountOf(d.creditAmount), 0);
  const difference = Math.round((totalDebit - totalCredit) * 100) / 100;

  const accountOptions = accounts
    .slice()
    .sort((a, b) => a.code.localeCompare(b.code))
    .map(account => ({ value: account.id, label: `${account.code} ${account.name}` }));

  const onSubmit = (data: FormData) => {
    const lines = data.details.filter(d => d.accountId || amountOf(d.debitAmount) || amountOf(d.creditAmount));
    if (lines.some(d => !d.accountId || (amountOf(d.debitAmount) > 0) === (amountOf(d.creditAmount) > 0))) {
      setError('details', { type: 'manual', message: t('journals.dialog.lineInvalid') });
      return;
    }
    if (lines.length < 2) {
      setError('details', { type: 'manual', message: t('journals.dialog.minDetails') });
      return;
    }
    if (difference !== 0) {
      setError('details', { type: 'manual', message: t('journals.dialog.unbalanced') });
      return;
    }

    const payload = {
      date: toUtcMidnight(data.date),
      description: data.description.trim(),
      reference: data.reference.trim() || null,
      details: lines.map(d => ({
        accountId: d.accountId,
        debitAmount: amountOf(d.debitAmount),
        creditAmount: amountOf(d.creditAmount),
        description: d.description.trim() || null,
      })),
    };
    const optimistic = {
      ...payload,
      totalAmount: totalDebit,
      details: payload.details.map((d, index) => ({ ...d, id: `temp-detail-${index}`, account: accounts.find(a => a.id === d.accountId) })),
    };
    const store = useJournalStoreBase.getState();
    onClose();

    if (journal) {
      const original = { ...journal };
      store.updateItem(journal.id, { ...optimistic, reference: payload.reference ?? journal.reference } as Partial<JournalEntry>);
      updateJournalEntry(journal.id, payload)
        .then(response => {
          useJournalStoreBase.getState().updateItem(journal.id, response.data);
          toast.success(t('common.messages.updateSuccess'));
          notifyDataChanged('journals');
        })
        .catch(error => {
          useJournalStoreBase.getState().updateItem(journal.id, original);
          toast.error(apiErrorMessage(error, 'common.messages.updateFailed'));
        });
      return;
    }

    const tempId = `temp-${Date.now()}`;
    store.addItem({ ...optimistic, id: tempId, userId: '', isPosted: false } as JournalEntry);
    createJournalEntry(payload)
      .then(response => {
        useJournalStoreBase.getState().updateItem(tempId, response.data);
        toast.success(t('common.messages.createSuccess'));
        notifyDataChanged('journals');
      })
      .catch(error => {
        removeListItem(useJournalStoreBase, tempId);
        toast.error(apiErrorMessage(error, 'common.messages.createFailed'));
      });
  };

  const title = readOnly ? t('journals.dialog.title.view') : isEditMode ? t('journals.dialog.title.edit') : t('journals.dialog.title.add');
  const amountField = (index: number, name: 'debitAmount' | 'creditAmount') => {
    const field = register(`details.${index}.${name}` as const);
    return (
      <Input
        {...field}
        inputMode="decimal"
        aria-label={t(name === 'debitAmount' ? 'journals.dialog.debitAmount' : 'journals.dialog.creditAmount')}
        className="text-right tabular-nums"
        disabled={readOnly}
        onChange={event => {
          event.target.value = sanitizeAmount(event.target.value);
          clearErrors('details');
          return field.onChange(event);
        }}
      />
    );
  };

  return (
    <>
      <DialogShell onClose={handleClose} className="max-w-4xl">
        <DialogHeader title={title} onClose={handleClose} closeLabel={t('common.actions.close')} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {journal?.isPosted && <p className="text-sm text-fg-secondary">{t('journals.dialog.postedHint')}</p>}

            <div className="grid grid-cols-1 md:grid-cols-[10rem_1fr_12rem] gap-4">
              <FormFieldWithError label={t('journals.dialog.transactionDate')} required error={errors.date?.message} htmlFor="date">
                <Input
                  id="date"
                  type="date"
                  disabled={readOnly}
                  {...register('date', { required: t('common.forms.validation.required') })}
                  variant={errors.date ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('journals.dialog.descriptionLabel')} required error={errors.description?.message} htmlFor="description">
                <Input
                  id="description"
                  autoFocus={!isEditMode}
                  disabled={readOnly}
                  {...register('description', { validate: value => !!value.trim() || t('common.forms.validation.required') })}
                  variant={errors.description ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError
                label={t('journals.dialog.reference')}
                helpText={isEditMode ? undefined : t('journals.dialog.referenceHint')}
                htmlFor="reference"
              >
                <Input id="reference" disabled={readOnly} {...register('reference')} />
              </FormFieldWithError>
            </div>

            {/* 分錄明細：科目、借方、貸方、摘要；金額靠右（§3-2） */}
            <div>
              <div className="overflow-x-auto">
                <div className="min-w-[640px]">
                  <div className="grid grid-cols-[minmax(0,1fr)_8.5rem_8.5rem_minmax(0,0.8fr)_2rem] gap-3 pb-2 text-sm font-medium text-fg-secondary">
                    <span>{t('journals.dialog.account')} <span className="text-red-500">*</span></span>
                    <span className="text-right">{t('journals.dialog.debitAmount')}</span>
                    <span className="text-right">{t('journals.dialog.creditAmount')}</span>
                    <span>{t('journals.dialog.description')}</span>
                    <span />
                  </div>
                  <div className="space-y-2">
                    {fields.map((field, index) => (
                      <div key={field.id} className="grid grid-cols-[minmax(0,1fr)_8.5rem_8.5rem_minmax(0,0.8fr)_2rem] gap-3 items-center">
                        <CustomSelect
                          value={details[index]?.accountId ?? ''}
                          onChange={value => {
                            setValue(`details.${index}.accountId`, value, { shouldDirty: true });
                            clearErrors('details');
                          }}
                          placeholder={t('journals.dialog.selectAccount')}
                          options={accountOptions}
                          disabled={readOnly}
                          searchable
                          searchPlaceholder={t('accounts.list.searchPlaceholder')}
                        />
                        {amountField(index, 'debitAmount')}
                        {amountField(index, 'creditAmount')}
                        <Input disabled={readOnly} aria-label={t('journals.dialog.description')} {...register(`details.${index}.description` as const)} />
                        {!readOnly && fields.length > 2 ? (
                          <button
                            type="button"
                            onClick={() => remove(index)}
                            className="h-10 w-8 inline-flex items-center justify-center text-fg-secondary hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            aria-label={t('journals.dialog.removeDetail')}
                            title={t('journals.dialog.removeDetail')}
                          >
                            <XMarkIcon className="h-4 w-4" />
                          </button>
                        ) : <span />}
                      </div>
                    ))}
                  </div>

                  {/* 合計列：借方、貸方對齊上面的欄位；不平衡時寫出差額（不只靠顏色） */}
                  <div className="mt-3 pt-3 border-t border-line grid grid-cols-[minmax(0,1fr)_8.5rem_8.5rem_minmax(0,0.8fr)_2rem] gap-3 items-center text-sm">
                    <div>
                      {!readOnly && (
                        <button
                          type="button"
                          onClick={() => append(emptyLine())}
                          className="inline-flex items-center gap-1 text-sm font-medium text-accent hover:text-brand-700 dark:hover:text-brand-300"
                        >
                          <PlusIcon className="h-4 w-4" />
                          {t('journals.dialog.addDetail')}
                        </button>
                      )}
                    </div>
                    <span className="text-right font-medium text-fg"><Amount value={totalDebit} /></span>
                    <span className="text-right font-medium text-fg"><Amount value={totalCredit} /></span>
                    {/* 還沒輸入金額時不顯示「借貸平衡」 */}
                    <span className={difference === 0 ? 'text-fg-secondary' : 'text-red-600 dark:text-red-400'}>
                      {totalDebit === 0 && totalCredit === 0
                        ? null
                        : difference === 0
                          ? t('journals.dialog.balanced')
                          : t('journals.dialog.difference', { amount: Math.abs(difference).toLocaleString() })}
                    </span>
                    <span />
                  </div>
                </div>
              </div>
              {errors.details?.message && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{errors.details.message}</p>}
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={handleClose} className={dialogButtonClass.cancel}>
              {readOnly ? t('common.actions.close') : t('common.actions.cancel')}
            </button>
            {!readOnly && (
              <button type="submit" className={dialogButtonClass.primary}>
                {isEditMode ? t('common.actions.save') : t('common.actions.create')}
              </button>
            )}
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('modal:unsavedChanges.title')}
        message={t('modal:unsavedChanges.message')}
        confirmText={t('modal:unsavedChanges.confirmButton')}
        cancelText={t('modal:unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default JournalDialog;
